import { RefObject, useEffect, useRef } from 'react';

import scrollExcessHandler from '../../mobile/boardsPage/editing/scrollExcessHandler';
import { SheetId } from '../../mobile/structural/sheet/sheetTypes';

type UseScopedStandardInputScrollExcessProps = {
    enabled: boolean;
    scrollableParentRef: RefObject<HTMLDivElement>;
    sheetId: SheetId;
};

type KeyboardDidShowEvent = CustomEvent<{
    keyboardHeight: number;
}>;

/**
 * Adds keyboard-sized scroll excess to a specific scrollable container when an input
 * inside that container is focused. This allows the user to manually scroll obscured fields into view
 * while the keyboard is open, without forcing an automatic scroll.
 * This should be used for standard input elements, as we have handlers in the middleware for mn elements
 */
const useSheetScrollExcess = ({
    enabled,
    scrollableParentRef,
    sheetId,
}: UseScopedStandardInputScrollExcessProps): void => {
    const appliedKeyboardHeightRef = useRef<number | null>(null);

    useEffect(() => {
        if (!enabled) return;

        const applyScrollExcess = async (keyboardHeight: number) => {
            if (!scrollableParentRef.current) return;
            if (!keyboardHeight) return;
            if (appliedKeyboardHeightRef.current === keyboardHeight) return;

            const activeElement = document.activeElement;
            if (!activeElement || !scrollableParentRef.current.contains(activeElement)) return;

            await scrollExcessHandler.apply(
                {
                    scrollableParent: scrollableParentRef.current,
                    bottomObstructionHeight: keyboardHeight,
                },
                sheetId,
            );

            appliedKeyboardHeightRef.current = keyboardHeight;
        };

        const onKeyboardDidShow = (event: Event) => {
            const keyboardHeight = (event as KeyboardDidShowEvent).detail?.keyboardHeight;
            if (!keyboardHeight) return;

            applyScrollExcess(keyboardHeight);
        };

        const cleanupScrollExcess = () => {
            if (!scrollableParentRef.current) return;

            appliedKeyboardHeightRef.current = null;

            return scrollExcessHandler.cleanup({ scrollableParent: scrollableParentRef.current, sheetId });
        };

        const onKeyboardWillHide = () => {
            cleanupScrollExcess();
        };

        window.addEventListener('keyboardDidShow', onKeyboardDidShow);
        window.addEventListener('keyboardWillHide', onKeyboardWillHide);

        return () => {
            window.removeEventListener('keyboardDidShow', onKeyboardDidShow);
            window.removeEventListener('keyboardWillHide', onKeyboardWillHide);
            cleanupScrollExcess();
        };
    }, [enabled, scrollableParentRef, sheetId]);
};

export default useSheetScrollExcess;
